import { useEffect, useState } from 'react';
import { useAuthContext } from '@/contexts/AuthContext';
import { supabase } from '@/lib/supabase';
import { AppLayout } from '@/components/feature/AppLayout';

interface KPIData {
  totalLeads: number;
  totalClients: number;
  activeProjects: number;
  backlogPending: number;
  tasksInProgress: number;
  tasksDone: number;
  totalCredits: number;
  totalDebits: number;
  balance: number;
}

export default function AdminDashboardPage() {
  const { profile } = useAuthContext();
  const [kpi, setKpi] = useState<KPIData>({
    totalLeads: 0,
    totalClients: 0,
    activeProjects: 0,
    backlogPending: 0,
    tasksInProgress: 0,
    tasksDone: 0,
    totalCredits: 0,
    totalDebits: 0,
    balance: 0,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!profile?.is_active) return;
    loadKPIs();
  }, [profile]);

  const loadKPIs = async () => {
    setLoading(true);

    const [leadsRes, clientsRes, projectsRes, tasksRes, financeRes] = await Promise.all([
      supabase.from('leads').select('*', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('clients').select('*', { count: 'exact', head: true }).eq('is_active', true),
      supabase.from('projects').select('*', { count: 'exact', head: true }).eq('status', 'ativo'),
      supabase.from('tasks').select('status'),
      supabase.from('financial_entries').select('type, amount, is_paid').eq('is_active', true),
    ]);

    const backlogCount = tasksRes.data?.filter((t) => t.status === 'backlog').length || 0;
    const doingCount = tasksRes.data?.filter((t) => t.status === 'doing').length || 0;
    const doneCount = tasksRes.data?.filter((t) => t.status === 'done').length || 0;

    const credits =
      financeRes.data
        ?.filter((f) => f.type === 'credit' && f.is_paid)
        .reduce((sum, f) => sum + (f.amount || 0), 0) || 0;
    const debits =
      financeRes.data
        ?.filter((f) => f.type === 'debit' && f.is_paid)
        .reduce((sum, f) => sum + (f.amount || 0), 0) || 0;

    setKpi({
      totalLeads: leadsRes.count || 0,
      totalClients: clientsRes.count || 0,
      activeProjects: projectsRes.count || 0,
      backlogPending: backlogCount,
      tasksInProgress: doingCount,
      tasksDone: doneCount,
      totalCredits: credits,
      totalDebits: debits,
      balance: credits - debits,
    });
    setLoading(false);
  };

  const formatCurrency = (value: number) =>
    new Intl.NumberFormat('pt-BR', { style: 'currency', currency: 'BRL' }).format(value);

  const formatDate = (dateStr: string) => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZone: 'America/Sao_Paulo',
    });
  };

  const kpiCards = [
    { label: 'Total de Leads', value: kpi.totalLeads, icon: 'ri-file-list-3-line', color: 'bg-sky-50 border-sky-100', iconColor: 'text-sky-500' },
    { label: 'Clientes Ativos', value: kpi.totalClients, icon: 'ri-building-line', color: 'bg-green-50 border-green-100', iconColor: 'text-green-500' },
    { label: 'Projetos Ativos', value: kpi.activeProjects, icon: 'ri-folder-line', color: 'bg-violet-50 border-violet-100', iconColor: 'text-violet-500' },
    { label: 'Backlog Pendente', value: kpi.backlogPending, icon: 'ri-archive-line', color: 'bg-orange-50 border-orange-100', iconColor: 'text-orange-500' },
    { label: 'Em Andamento', value: kpi.tasksInProgress, icon: 'ri-loader-2-line', color: 'bg-yellow-50 border-yellow-100', iconColor: 'text-yellow-500' },
    { label: 'Tarefas Concluídas', value: kpi.tasksDone, icon: 'ri-check-double-line', color: 'bg-emerald-50 border-emerald-100', iconColor: 'text-emerald-500' },
  ];

  return (
    <AppLayout>
      <div className="mb-8">
        <h1 className="text-slate-800 text-2xl font-bold">Dashboard Administrativo</h1>
        <p className="text-slate-500 text-sm mt-1">Visão geral de todos os indicadores do sistema</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-20">
          <i className="ri-loader-4-line animate-spin text-cyan-500 text-2xl"></i>
        </div>
      ) : (
        <>
          {/* KPI Grid */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-4 mb-6">
            {kpiCards.map((card) => (
              <div key={card.label} className="p-5 rounded-xl border border-slate-200 bg-white">
                <div className={`w-10 h-10 flex items-center justify-center rounded-lg border mb-3 ${card.color}`}>
                  <i className={`${card.icon} ${card.iconColor}`}></i>
                </div>
                <p className="text-slate-500 text-xs mb-1">{card.label}</p>
                <p className="text-slate-800 text-2xl font-bold">{card.value}</p>
              </div>
            ))}
          </div>

          {/* Finance Row */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-cyan-50 border border-cyan-100 mb-3">
                <i className="ri-arrow-up-circle-line text-cyan-500"></i>
              </div>
              <p className="text-slate-500 text-xs mb-1">Créditos Totais</p>
              <p className="text-cyan-600 text-xl font-bold">{formatCurrency(kpi.totalCredits)}</p>
            </div>

            <div className="p-5 rounded-xl border border-slate-200 bg-white">
              <div className="w-10 h-10 flex items-center justify-center rounded-lg bg-red-50 border border-red-100 mb-3">
                <i className="ri-arrow-down-circle-line text-red-500"></i>
              </div>
              <p className="text-slate-500 text-xs mb-1">Débitos Totais</p>
              <p className="text-red-600 text-xl font-bold">{formatCurrency(kpi.totalDebits)}</p>
            </div>

            <div className={`p-5 rounded-xl border bg-white ${kpi.balance >= 0 ? 'border-emerald-200' : 'border-red-200'}`}>
              <div className={`w-10 h-10 flex items-center justify-center rounded-lg mb-3 ${kpi.balance >= 0 ? 'bg-emerald-50 border border-emerald-100' : 'bg-red-50 border border-red-100'}`}>
                <i className={`ri-wallet-3-line ${kpi.balance >= 0 ? 'text-emerald-500' : 'text-red-500'}`}></i>
              </div>
              <p className="text-slate-500 text-xs mb-1">Saldo em Caixa</p>
              <p className={`text-2xl font-bold ${kpi.balance >= 0 ? 'text-emerald-600' : 'text-red-600'}`}>
                {formatCurrency(kpi.balance)}
              </p>
            </div>
          </div>

          {/* Session Info */}
          <div className="p-5 rounded-xl border border-slate-200 bg-white">
            <h3 className="text-slate-800 font-semibold mb-4 text-sm">Informações da Sessão</h3>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-sm">
              <div>
                <span className="text-slate-400 text-xs">Usuário</span>
                <p className="text-slate-700 font-medium mt-0.5">{profile?.full_name || profile?.email}</p>
              </div>
              <div>
                <span className="text-slate-400 text-xs">Papel</span>
                <p className="text-slate-700 font-medium mt-0.5">
                  {profile?.role === 'admin' ? 'Administrador' : 'Usuário'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 text-xs">Status</span>
                <p className={`font-medium mt-0.5 ${profile?.is_active ? 'text-emerald-600' : 'text-red-600'}`}>
                  {profile?.is_active ? 'Ativo' : 'Inativo'}
                </p>
              </div>
              <div>
                <span className="text-slate-400 text-xs">Última atualização</span>
                <p className="text-slate-700 mt-0.5">{formatDate(profile?.updated_at || '')}</p>
              </div>
            </div>
          </div>
        </>
      )}
    </AppLayout>
  );
}
